import React, { useEffect, useState, memo } from "react";
import {
  Box, Button, Dialog, DialogTitle,
  DialogActions, DialogContent, TextField
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import DefaultActions from "../../../components/Card/DefaultActions";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import AdyenPaymentProviderWizard from "../../../components/Wizards/PaymentProvider/AdyenPaymentProviderWizard";
import KlarnaPaymentProviderWizard from "../../../components/Wizards/PaymentProvider/KlarnaPaymentProviderWizard";
import PayPalPaymentProviderWizard from "../../../components/Wizards/PaymentProvider/PayPalPaymentProviderWizard";
import DefaultPaymentProviderWizard from "../../../components/Wizards/PaymentProvider/DefaultPaymentProviderWizard";
import QliroPaymentProviderWizard from "../../../components/Wizards/PaymentProvider/QliroPaymentProviderWizard";

const PaymentProviderConfigEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { id = null, paymentProviderType = null } = params || {};
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<PaymentProviderConfigViewModel | null>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      authService
        .get<PaymentProviderConfigViewModel>(`/settingsApi/GetPaymentProviderConfig?id=${id}`)
        .then(setViewModel)
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      setViewModel({ createNew: true, paymentProvider: paymentProviderType });
    }
  }, [id, paymentProviderType, authService]);

  const handleSave = async (model?: PaymentProviderConfigViewModel) => {
    const toSave = model ?? viewModel;
    if (!toSave) return;
    setIsLoading(true);
    try {
      const response = await authService.post<PaymentProviderConfigViewModel>(
        "/settingsApi/CreateOrUpdatePaymentProviderConfig",
        toSave
      );
      setViewModel(response);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (toSave.createNew) {
        navigate({ path: "/settings/paymentproviderconfig/edit", params: { id: response.id } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;

    if (viewModel?.createNew) {
      navigate({ path: "/settings/paymentproviderconfig/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeletePaymentProviderConfig`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
      navigate({ path: "/settings/paymentproviderconfig/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (newDescription?: string) => {
    const newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyRequiresSavedItem"), "warning");
      return;
    }

    if (newDescription && newDescription === viewModel?.description) {
      showSnackbar(t("dialog.descriptionSameAsSource"), "warning");
      return;
    }

    if (newDescription) {
      setViewModel((prev) => ({ ...prev, newDescription }));
    }

    if (!viewModel?.newDescription && !newDescription) {
      setIsCopyModalOpen(true);
      return;
    }

    setIsCopyModalOpen(false);
    setIsLoading(true);

    try {
      const response = await authService.post<PaymentProviderConfigViewModel>(
        "/settingsApi/CopyPaymentProviderConfig",
        { ...viewModel, newDescription }
      );

      setViewModel(response);
      showSnackbar(t("dialog.configCopiedSuccess"), "success");
      setIsLoading(false);
      navigate({ path: "/settings/paymentproviderconfig/edit", params: { id: response.id } });
    } catch (error: any) {
      showSnackbar(`${t("dialog.configCopyFailed")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderWizard = () => {
    const provider = viewModel?.paymentProvider;
    //just do double == as sometimes params get cast as strings
    if (provider == 50) return <AdyenPaymentProviderWizard viewModel={viewModel} onSave={handleSave} />;
    if (provider == 11) return <KlarnaPaymentProviderWizard viewModel={viewModel} onSave={handleSave} />;
    if (provider == 40) return <PayPalPaymentProviderWizard viewModel={viewModel} onSave={handleSave} />;
    if (provider == 30) return <QliroPaymentProviderWizard viewModel={viewModel} onSave={handleSave} />;
    //if (provider == 70) return <SwishPaymentProviderWizard viewModel={viewModel} onSave={handleSave} />;
    return <DefaultPaymentProviderWizard viewModel={viewModel} onSave={handleSave} />;
  };

  type PaymentProviderCopyModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (newDescription: string) => void;
    viewModel?: PaymentProviderConfigViewModel | null;
  };

  const PaymentProviderConfigCopyModal = memo(({ open, onClose, onConfirm, viewModel }: PaymentProviderCopyModalProps) => {
    const { t } = useTranslation();
    const [tempDescription, setTempDescription] = useState("");

    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t("common.enterNewDescription")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.newDescription")}
            fullWidth
            variant="outlined"
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
            error={!tempDescription.trim()}
            helperText={
              !tempDescription.trim()
                ? t("help.descriptionRequired")
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            onClick={() => {
              if (!tempDescription.trim()) {
                showSnackbar(t("dialog.descriptionMissing"), "warning");
                return;
              }
              onConfirm(tempDescription.trim());
            }}
            color="primary"
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  return (
    <>
      <CenteredCard title={t("common.edit") + " " + t("common.paymentProviderConfig")} isLoading={isLoading}>
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", maxWidth: "fit-content", mb: 2 }}>
          <DefaultActions onDelete={handleDelete} onCopy={handleCopy} />
        </Box>
        <PaymentProviderConfigCopyModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} viewModel={viewModel} />
        {viewModel && !isLoading && renderWizard()}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>

          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>

    </>
  );
};

export default PaymentProviderConfigEditPage;
